// ALSOK Corporate Theme Definition
// セキュリティ業界の信頼性と専門性を表現するカラーパレット

export const alsokTheme = {
  // Primary Colors - ALSOK公式ネイビーブルー
  primary: {
    main: '#1B365D',      // ALSOK公式メインネイビー
    light: '#2E4B69',     // ライトネイビー
    dark: '#0F2A47',      // ダークネイビー
    contrast: '#FFFFFF'   // コントラスト用ホワイト
  },
  
  // Secondary Colors - ALSOKライトブルー系
  secondary: {
    main: '#4A90A4',      // ALSOK公式ライトブルー
    light: '#6BA5B8',     // ライトブルー明
    dark: '#357B8F',      // ライトブルー暗
    contrast: '#FFFFFF'   // コントラスト用ホワイト
  },
  
  // Accent Colors - セキュリティ業界対応色
  accent: {
    primary: '#E74C3C',   // 緊急・警告用赤
    secondary: '#F39C12', // 注意用オレンジ
    success: '#27AE60',   // 成功・安全用グリーン
    info: '#3498DB',      // 情報用ブルー
    warning: '#F1C40F'    // 警告用イエロー
  },
  
  // Neutral Colors - ニュートラルカラー
  neutral: {
    white: '#FFFFFF',
    lightGray: '#F8F9FA',
    gray: '#6C757D',
    mediumGray: '#495057',
    darkGray: '#343A40',
    black: '#000000'
  },
  
  // Status Colors - ステータス表示色
  status: {
    success: '#28A745',
    warning: '#FFC107',
    error: '#DC3545',
    info: '#17A2B8',
    processing: '#6F42C1'
  },
  
  // Gradients - 新しいALSOKカラーベースのグラデーション
  gradients: {
    primary: 'linear-gradient(135deg, #1B365D 0%, #2E4B69 100%)',
    secondary: 'linear-gradient(135deg, #4A90A4 0%, #6BA5B8 100%)',
    accent: 'linear-gradient(135deg, #E74C3C 0%, #F39C12 100%)',
    neutral: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)',
    security: 'linear-gradient(135deg, #0F2A47 0%, #1B365D 50%, #2E4B69 100%)',
    clean: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)'  // お客様受付用
  },
  
  // Typography - 日本語最適化タイポグラフィ
  typography: {
    fontFamily: {
      primary: '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Noto Sans JP", "Yu Gothic", "Meiryo", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      heading: '"Hiragino Sans", "Hiragino Kaku Gothic ProN", "Noto Sans JP", "Yu Gothic", "Meiryo", sans-serif',
      monospace: '"Consolas", "Monaco", "Courier New", "SF Mono", monospace'
    },
    fontSize: {
      xs: '0.75rem',    // 12px - アクセシビリティ考慮
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px - 基準サイズ
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem'   // 36px
    },
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800
    }
  },
  
  // Spacing - スペーシング
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    '3xl': '64px'
  },
  
  // Border Radius - 角丸
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '16px',
    full: '50%'
  },
  
  // Shadows - 影
  shadows: {
    sm: '0 1px 3px rgba(0,0,0,0.1)',
    md: '0 4px 8px rgba(0,0,0,0.1)',
    lg: '0 8px 16px rgba(0,0,0,0.1)',
    xl: '0 16px 32px rgba(0,0,0,0.1)',
    security: '0 4px 20px rgba(0,51,102,0.2)',
    inset: 'inset 0 2px 4px rgba(0,0,0,0.1)'
  },
  
  // Animation - アニメーション
  animation: {
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    transitionFast: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    transitionSlow: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    // アニメーション定義
    keyframes: {
      fadeIn: `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `,
      slideUp: `
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `,
      pulse: `
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.7; transform: scale(1.05); }
        }
      `,
      glow: `
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 5px rgba(0,102,204,0.3); }
          50% { box-shadow: 0 0 20px rgba(0,102,204,0.6), 0 0 30px rgba(0,102,204,0.4); }
        }
      `,
      breathe: `
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }
      `
    }
  },
  
  // Breakpoints - レスポンシブブレークポイント
  breakpoints: {
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
    xxl: '1400px'
  }
}

// UI Component Design Tokens
export const uiTokens = {
  // インタラクションステート
  interaction: {
    hover: {
      transform: 'translateY(-2px)',
      shadowElevation: '0 4px 12px rgba(0,0,0,0.15)'
    },
    active: {
      transform: 'translateY(0)',
      shadowElevation: '0 2px 4px rgba(0,0,0,0.1)'
    },
    focus: {
      outline: `2px solid ${alsokTheme.primary.light}`,
      outlineOffset: '2px'
    }
  },
  
  // レイアウトパターン
  layout: {
    header: {
      height: '80px',
      padding: '0 30px',
      background: 'rgba(0,51,102,0.95)',
      backdropFilter: 'blur(10px)'
    },
    sidebar: {
      width: '300px',
      background: 'rgba(255,255,255,0.95)',
      backdropFilter: 'blur(10px)'
    },
    card: {
      padding: '20px 25px',
      borderRadius: alsokTheme.borderRadius.lg,
      background: alsokTheme.neutral.white,
      shadow: alsokTheme.shadows.md
    }
  },
  
  // ステータス表示
  statusIndicator: {
    size: {
      small: '12px',
      medium: '16px',
      large: '20px'
    },
    colors: {
      active: alsokTheme.accent.green,
      warning: alsokTheme.status.warning,
      error: alsokTheme.status.error,
      processing: alsokTheme.status.processing
    }
  }
}

// Component-specific theme variants
export const componentThemes = {
  // ボタンテーマ
  button: {
    primary: {
      background: alsokTheme.primary.main,
      hover: alsokTheme.primary.light,
      active: alsokTheme.primary.dark,
      text: alsokTheme.primary.contrast
    },
    secondary: {
      background: alsokTheme.secondary.main,
      hover: alsokTheme.secondary.light,
      active: alsokTheme.secondary.dark,
      text: alsokTheme.secondary.contrast
    },
    accent: {
      background: alsokTheme.accent.orange,
      hover: '#ff8555',
      active: '#e55a2b',
      text: alsokTheme.neutral.white
    },
    ghost: {
      background: 'transparent',
      hover: alsokTheme.neutral.lightGray,
      active: alsokTheme.neutral.gray,
      text: alsokTheme.primary.main,
      border: alsokTheme.primary.main
    }
  },
  
  // アバターテーマ
  avatar: {
    background: alsokTheme.gradients.security,
    face: alsokTheme.neutral.white,
    pulse: alsokTheme.primary.light,
    statusColors: {
      ready: alsokTheme.status.success,
      processing: alsokTheme.status.processing,
      listening: alsokTheme.status.info,
      speaking: alsokTheme.accent.green,
      error: alsokTheme.status.error
    }
  },
  
  // カードテーマ
  card: {
    background: alsokTheme.neutral.white,
    border: alsokTheme.neutral.lightGray,
    shadow: alsokTheme.shadows.md,
    header: alsokTheme.gradients.neutral
  },
  
  // ログイン画面テーマ - 簡潔で分かりやすく
  login: {
    background: alsokTheme.gradients.security,
    card: {
      background: alsokTheme.neutral.white,
      borderRadius: alsokTheme.borderRadius.xl,
      shadow: '0 20px 60px rgba(27, 54, 93, 0.15)',
      maxWidth: '400px',
      padding: '2rem'
    },
    input: {
      background: alsokTheme.neutral.white,
      border: alsokTheme.neutral.lightGray,
      borderRadius: alsokTheme.borderRadius.md,
      focus: alsokTheme.primary.main,
      fontSize: alsokTheme.typography.fontSize.base,
      padding: '12px 16px'
    },
    button: {
      minHeight: '48px',
      fontSize: alsokTheme.typography.fontSize.lg,
      fontWeight: alsokTheme.typography.fontWeight.semibold
    }
  },
  
  // 管理画面テーマ - 操作性・視認性重視
  admin: {
    background: 'linear-gradient(135deg, #F8FAFB 0%, #E5EBF0 100%)',
    header: alsokTheme.gradients.security,
    sidebar: {
      background: alsokTheme.neutral.white,
      width: '280px',
      shadow: alsokTheme.shadows.lg
    },
    statCard: {
      background: alsokTheme.neutral.white,
      border: `1px solid ${alsokTheme.neutral.lightGray}`,
      borderRadius: alsokTheme.borderRadius.lg,
      shadow: alsokTheme.shadows.sm,
      hoverShadow: alsokTheme.shadows.lg,
      padding: '20px',
      transition: alsokTheme.animation.transition
    },
    table: {
      headerBackground: alsokTheme.primary.main,
      headerText: alsokTheme.neutral.white,
      rowHover: 'rgba(27, 54, 93, 0.05)',
      borderColor: alsokTheme.neutral.lightGray
    }
  },
  
  // お客様受付画面テーマ - 清潔感・わかりやすさ重視
  reception: {
    background: alsokTheme.gradients.clean,
    container: {
      background: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: alsokTheme.borderRadius.xl,
      shadow: '0 20px 60px rgba(27, 54, 93, 0.1)'
    },
    avatarArea: {
      background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
      borderRadius: alsokTheme.borderRadius.lg
    },
    sidebar: {
      background: 'rgba(255, 255, 255, 0.98)',
      backdropFilter: 'blur(15px)',
      borderLeft: `3px solid ${alsokTheme.primary.light}`
    },
    controls: {
      voice: {
        background: `linear-gradient(135deg, ${alsokTheme.info} 0%, #2980B9 100%)`,
        shadow: '0 4px 15px rgba(52, 152, 219, 0.3)',
        minHeight: '48px'
      },
      face: {
        background: `linear-gradient(135deg, ${alsokTheme.accent.success} 0%, #1E8449 100%)`,
        shadow: '0 4px 15px rgba(39, 174, 96, 0.3)',
        minHeight: '48px'
      },
      reservation: {
        background: `linear-gradient(135deg, ${alsokTheme.secondary.main} 0%, ${alsokTheme.secondary.dark} 100%)`,
        shadow: '0 4px 15px rgba(74, 144, 164, 0.3)',
        minHeight: '48px'
      }
    },
    quickActions: {
      background: alsokTheme.primary.main,
      hover: alsokTheme.primary.light,
      text: alsokTheme.neutral.white,
      minHeight: '44px',
      borderRadius: alsokTheme.borderRadius.md
    }
  }
}

// Theme utility helpers
export const themeHelpers = {
  // メディアクエリヘルパー
  mediaQuery: {
    sm: `@media (min-width: ${alsokTheme.breakpoints.sm})`,
    md: `@media (min-width: ${alsokTheme.breakpoints.md})`,
    lg: `@media (min-width: ${alsokTheme.breakpoints.lg})`,
    xl: `@media (min-width: ${alsokTheme.breakpoints.xl})`,
    maxSm: `@media (max-width: ${parseInt(alsokTheme.breakpoints.sm) - 1}px)`,
    maxMd: `@media (max-width: ${parseInt(alsokTheme.breakpoints.md) - 1}px)`,
    maxLg: `@media (max-width: ${parseInt(alsokTheme.breakpoints.lg) - 1}px)`
  },
  
  // カラーアルファ値ヘルパー
  withOpacity: (color, opacity) => {
    // HEX色をRGBAに変換してアルファ値を適用
    const hex = color.replace('#', '')
    const r = parseInt(hex.substr(0, 2), 16)
    const g = parseInt(hex.substr(2, 2), 16)
    const b = parseInt(hex.substr(4, 2), 16)
    return `rgba(${r}, ${g}, ${b}, ${opacity})`
  },
  
  // ダークモード対応ヘルパー（将来の拡張用）
  getDarkMode: (lightColor, darkColor) => {
    return `var(--theme-mode, ${lightColor})`
  }
}

export default alsokTheme